"use client";

import { useEffect, useRef } from "react";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import { usePlatformDataTracking } from "@/lib/PlatformData/platformDataStore";
import { logToSentry } from "../platformDataErrorTracking";
import { PlatformDataEvents } from "../PlatformDataEvents";
import { format } from "date-fns";
import initialState from "@/lib/StaticData/searchFormData";
import { useTrackChannelChange } from "../userEventTracking";
import { ViewModeConfig } from "@/lib/Helpers/currentUrl";
import {
  getRoomAllocationsFromRooms,
  getTravellersFromRooms,
} from "@/lib/Helpers/urlHelpers";
import { ServerConfig } from "@/lib/Helpers/getServerConfig";


type SearchRefinementMappings = {
  country: { action: string; category: string };
  region: { action: string; category: string };
  city: { action: string; category: string };
  cityFilter: { action: string; category: string };
  from: { action: string; category: string };
  to: { action: string; category: string };
  duration: { action: string; category: string };
  departureAirport: { action: string; category: string };
  rooms: { action: string; category: string };
};

const searchRefinementMappings: SearchRefinementMappings = {
  country: { action: "destination", category: "search_refiner" },
  region: { action: "destination", category: "search_refiner" },
  city: { action: "destination", category: "search_refiner" },
  cityFilter: { action: "reiseziel", category: "search_refiner" },
  from: { action: "travel_period", category: "search_refiner" },
  to: { action: "travel_period", category: "search_refiner" },
  duration: { action: "duration", category: "search_refiner" },
  departureAirport: { action: "departure_airport", category: "search_refiner" },
  rooms: { action: "rooms", category: "search_refiner" },
};

export const usePlatformDataSearchTracking = (serverConfig: ServerConfig) => {
  const { pushEvent } = usePlatformDataTracking();
  const searchParams = useReactiveSearchParams();
  const { viewMode } = serverConfig;
  const formValues = initialState;
  const prevParamsRef = useRef<Record<string, string>>({});
  const trackChannelChange = useTrackChannelChange();
  const prevViewModeRef = useRef<ViewModeConfig | null>(null);

  useEffect(() => {
    if (!viewMode) return;

    try {
      const currentParams = Object.fromEntries(searchParams.entries());
      const prevParams = prevParamsRef.current;

      // Track only changed parameters
      const changedParams = Object.entries(currentParams).filter(
        ([key, value]) => prevParams[key] !== value
      );

      // Update prevParams for next comparison
      prevParamsRef.current = currentParams;

      // Skip if this is the initial load (prevParams is empty)
      if (Object.keys(prevParams).length === 0) return;

      // Track search refinement changes
      changedParams
        .filter(([key]) => key in searchRefinementMappings)
        .forEach(([key, value]) => {
          let label;
          switch (key) {
            case "country":
            case "region":
            case "city":
              label = value || "any";
              break;
            case "cityFilter":
              label = value === "false" ? "any" : value;
              break;
            case "from":
            case "to":
              // Only track travel period when both dates are present and 'to' date changes
              if (currentParams.from && currentParams.to && key === "to") {
                label = `${format(
                  new Date(currentParams.from),
                  "dd.MM.yyyy"
                )} - ${format(new Date(currentParams.to), "dd.MM.yyyy")}`;
              }
              break;
            case "departureAirport":
              if (value === "Nur Hotel") return;
              label = value;
              break;
            case "duration":
              const formValuesWithDuration = formValues as typeof formValues & {
                duration: {
                  byId: {
                    [key: string]: { label: string };
                  };
                };
              };
              const durationValue = value as
                | "false"
                | number
                | `${number}_${number}`;
              if (
                typeof formValuesWithDuration.duration?.byId[durationValue] !==
                "undefined"
              ) {
                label = formValuesWithDuration.duration.byId[
                  durationValue
                ].label.replace(" Tage", "");
              } else {
                label = value;
              }
              break;
            case "rooms":
              // Special handling for rooms - track both travellers and room allocations
              // Skip the default label assignment since we handled in this block as we need to push 2 events
              label = null;
              const rooms = value || "";
              const travellers = getTravellersFromRooms(rooms);
              const roomAllocations = getRoomAllocationsFromRooms(rooms);

              // Track travellers event
              if (travellers.length > 0) {
                pushEvent({
                  event: PlatformDataEvents.USER_INTERACTION,
                  user: {
                    interaction: {
                      action: "travellers",
                      category: "search_refiner",
                      label: travellers.join(","),
                      n: 0,
                    },
                  },
                });
              }

              // Track room allocations event
              if (roomAllocations.length > 0) {
                pushEvent({
                  event: PlatformDataEvents.USER_INTERACTION,
                  user: {
                    interaction: {
                      action: "travellerRoomAllocations",
                      category: "search_refiner",
                      label: roomAllocations.join(","),
                      n: 0,
                    },
                  },
                });
              }

              break;
            default:
              label = value;
          }

          if (label && !(key === "from")) {
            pushEvent({
              event: PlatformDataEvents.USER_INTERACTION,
              user: {
                interaction: {
                  action:
                    searchRefinementMappings[
                      key as keyof SearchRefinementMappings
                    ].action,
                  category:
                    searchRefinementMappings[
                      key as keyof SearchRefinementMappings
                    ].category,
                  label,
                  n: 0,
                },
              },
            });
          }
        });
    } catch (err) {
      logToSentry(err as Error, [
        ["function", "useSearchTracking"],
        ["status", "fail"],
      ]);
    }
  }, [searchParams, viewMode, pushEvent, formValues, trackChannelChange]);

  // Track channel changes based on viewMode
  useEffect(() => {
    if (!viewMode) return;

    if (prevViewModeRef.current === null) {
      prevViewModeRef.current = viewMode;
      return;
    }

    if (prevViewModeRef.current.channel !== viewMode.channel) {
      trackChannelChange({ channel: viewMode.channel });
    }

    prevViewModeRef.current = viewMode;
  }, [viewMode, trackChannelChange]);
};
